import React, { useState } from 'react';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { useFetch, usePaged } from '../../hooks';
import { Button, Card, SearchBox, Select } from '../../ui/basics';
import { DataTable } from '../../ui/DataTable';
import { Confirm, useToast } from '../../ui/overlay';
import RecordDrawer from './RecordDrawer';

/** One directory list (clients or recruiters): toolbar, paginated table, add / edit / delete.
 *  Props describe the table: kind (URL), noun, idKey, nameKey, sections, filters, columns(), rowActions?(row). */
export default function DirectoryList({ org, kind, noun, plural, idKey, nameKey, sections, filters, columns, initialOrdering, rowActions }) {
  const { user } = useAuth();
  const toast = useToast();
  const paged = usePaged(`${kind}/`, { fixed: { organization: org }, initialOrdering });
  const facets = useFetch(`${kind}/facets/`, { organization: org });
  const { filters: values, setFilter, clearFilters, reload } = paged;
  const [dialog, setDialog] = useState(null); // { type: 'form'|'delete', row? }
  const [busy, setBusy] = useState(false);
  const canEdit = user.can_edit;
  const done = () => { setDialog(null); reload(); facets.reload(); };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`${kind}/${dialog.row[idKey]}/`);
      toast(`${noun} deleted`, 'success');
      done();
    } catch (e) {
      toast(e.message, 'error');
      setDialog(null);
    }
    setBusy(false);
  };
  const patch = async (row, body, message) => {
    try { await api.patch(`${kind}/${row[idKey]}/`, body); toast(message, 'success'); reload(); facets.reload(); }
    catch (e) { toast(e.message, 'error'); }
  };

  const actions = canEdit ? [{
    key: 'actions', label: '', align: 'right', width: 150,
    render: (row) => (
      <div className="row" style={{ justifyContent: 'flex-end', gap: 2, flexWrap: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
        <Button variant="ghost" size="sm" icon="edit" aria-label={`Edit ${noun.toLowerCase()}`} onClick={() => setDialog({ type: 'form', row })} />
        {rowActions && rowActions(row, patch)}
        <Button variant="ghost" size="sm" icon="trash" aria-label={`Delete ${noun.toLowerCase()}`} onClick={() => setDialog({ type: 'delete', row })} />
      </div>),
  }] : [];

  return (
    <>
      <Card flush>
        <div className="toolbar">
          <SearchBox value={values.search} onChange={(v) => setFilter('search', v)} placeholder={`Search ${plural}…`} />
          {filters.map((f) => (
            <Select key={f.key} value={values[f.key] || ''} onChange={(e) => setFilter(f.key, e.target.value)}
              options={facets.data ? facets.data[f.key] || [] : []} placeholder={f.placeholder} aria-label={f.placeholder} />
          ))}
          {Object.values(values).some(Boolean) && <Button variant="ghost" size="sm" onClick={clearFilters}>Clear</Button>}
          <div className="right">{canEdit && <Button variant="primary" icon="plus" onClick={() => setDialog({ type: 'form' })}>Add {noun.toLowerCase()}</Button>}</div>
        </div>
        <DataTable columns={[...columns(org === 'combined'), ...actions]} paged={paged} rowKey={(r) => r[idKey]} noun={plural}
          onRowClick={canEdit ? (row) => setDialog({ type: 'form', row }) : undefined} />
      </Card>
      {dialog && dialog.type === 'form' && (
        <RecordDrawer kind={kind} noun={noun} record={dialog.row} org={org} sections={sections} idKey={idKey}
          suggestions={facets.data || {}} onClose={() => setDialog(null)} onSaved={done} />
      )}
      {dialog && dialog.type === 'delete' && (
        <Confirm danger busy={busy} title={`Delete ${noun.toLowerCase()}`} confirmLabel="Delete" onClose={() => setDialog(null)} onConfirm={remove}
          message={`Permanently delete ${dialog.row[nameKey]}? This cannot be undone. If placements still use it, the delete is refused.`} />
      )}
    </>
  );
}
